/* @flow */
import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { reaction, computed } from 'mobx';
import { Link } from 'react-router-dom';
import { translate } from 'react-i18next';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';
import RequireForAccess from 'react-access';
import type { TranslatorProps } from 'react-i18next';

import Loading from 'components/Loading/Loading';
import DashboardStore from 'stores/DashboardStore';
import UserStore from 'stores/UserStore';
import { createNewProject } from 'permissions/project.js';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import * as statuses from 'utils/statuses';

import ChartLegend from '../../components/ChartLegend/ChartLegend';
import { isIE } from '../../../../utils/browserDetection';
import ChartKeys from '../../components/ChartKeys';
import WidgetActions from '../../components/WidgetActions';
import NivoLineChart from '../../components/NivoLineChart';
import type { CommonWidgetProps } from '../../commonTypes';

import styles from './Projects.module.scss';

const STATIC_DATA = {
  projects: [
    {
      scheduled: 0,
      active: 0,
      ended: 0,
      archived: 0,
      date: '2021-03-08',
    },
  ],
};

export type ProjectsProps = CommonWidgetProps;

type InjectedProps = {
  dashboardStore: DashboardStore,
  userStore: UserStore,
} & TranslatorProps;

type State = {
  status: string,
  data: any[],
};

@translate()
@inject('dashboardStore', 'userStore')
@observer
export default class Projects extends ComponentWithInjectedProps<
  ProjectsProps,
  State,
  InjectedProps
> {
  static LOAD_THUMBNAIL = false;

  @computed get shouldLoadData(): boolean {
    const { size } = this.props;

    return size !== 'thumbnail' || Projects.LOAD_THUMBNAIL;
  }

  @computed get widgetData(): { projects: any[] | null } {
    const { dashboardStore } = this.props;

    if (!this.shouldLoadData) {
      return STATIC_DATA;
    }

    return {
      projects:
        dashboardStore && dashboardStore.projectsbytime
          ? dashboardStore.projectsbytime.projects
          : null,
    };
  }

  state = {
    status: 'Week',
    data: [],
  };

  autoUpdateDisposer: Function;

  componentDidMount() {
    const { dashboardStore } = this.props;

    this.loadWidgetData(dashboardStore.loadedTradingAccount, this.state.status);

    this.autoUpdateDisposer = reaction(
      () => dashboardStore.loadedTradingAccount,
      ta => {
        this.loadWidgetData(ta, this.state.status);
      }
    );
  }

  loadWidgetData = (tradingAccount: string, status: string) => {
    const { dashboardStore } = this.props;

    if (this.shouldLoadData) {
      dashboardStore.loadProjects(tradingAccount);
      this.changeData(status);
    }
  };

  componentWillUnmount() {
    this.autoUpdateDisposer();
  }

  getWeek = (time: Date) => {
    const onejan = new Date(time.getFullYear(), 0, 1);
    return Math.ceil(((time - onejan) / 86400000 + onejan.getDay() + 1) / 7);
  };

  async changeData(timePeriod: string) {
    const { dashboardStore } = this.props;

    this.setState({ status: timePeriod });

    await dashboardStore.loadProjects(dashboardStore.loadedTradingAccount, timePeriod);

    if (timePeriod === 'Week') {
      this.displayWeekView();
    }

    if (timePeriod === 'Month') {
      this.displayMonthView();
    }

    if (timePeriod === 'Year') {
      this.displayYearView();
    }
  }

  displayWeekView() {
    const { dashboardStore, i18n } = this.props;
    const { projects } = this.widgetData;
    const active = [];
    const scheduled = [];
    const ended = [];
    const archived = [];

    const dates = [];

    for (let i = 1; i <= 7; i++) {
      const day = new Date();
      day.setDate(day.getDate() - day.getDay() + i);
      dates.push(
        day.getFullYear() +
          '-' +
          ('0' + (day.getMonth() + 1)).slice(-2) +
          '-' +
          ('0' + day.getDate()).slice(-2)
      );
    }

    dates.map(date => {
      const dayArray = projects ? projects.filter(e => e.date.includes(date)) : null;

      const dateActive = {
        x: date,
        y: dayArray && dayArray[0] ? dayArray[0].active : 0,
      };

      const dateScheduled = {
        x: date,
        y: dayArray && dayArray[0] ? dayArray[0].scheduled : 0,
      };

      const dateEnded = {
        x: date,
        y: dayArray && dayArray[0] ? dayArray[0].ended : 0,
      };

      const dateArchived = {
        x: date,
        y: dayArray && dayArray[0] ? dayArray[0].archived : 0,
      };

      active.push(dateActive);
      scheduled.push(dateScheduled);
      ended.push(dateEnded);
      archived.push(dateArchived);

      return active;
    });

    const weekProjects = [
      {
        id: statuses.translate('active', i18n),
        color: 'hsl(12, 74%, 59%)',
        data: active,
      },
      {
        id: statuses.translate('scheduled', i18n),
        color: 'hsl(200, 70%, 70%)',
        data: scheduled,
      },
      {
        id: statuses.translate('ended', i18n),
        color: 'hsl(203, 54%, 46%)',
        data: ended,
      },
      {
        id: statuses.translate('archived', i18n),
        color: 'hsl(200, 7%, 67%)',
        data: archived,
      },
    ];

    this.setState({
      data: weekProjects,
    });
  }

  displayMonthView() {
    const { dashboardStore, i18n } = this.props;
    const { projects } = this.widgetData;
    const active = [];
    const scheduled = [];
    const ended = [];
    const archived = [];

    const time = new Date();
    const y = time.getFullYear();
    const m = time.getMonth();
    const dates = [];

    const firstWeekOfMonth = this.getWeek(new Date(y, m, 1));
    const lastWeekOfMonth = this.getWeek(new Date(y, m + 1, 0));

    for (let i = firstWeekOfMonth; i < lastWeekOfMonth; i++) {
      dates.push(i);
    }

    dates.map(date => {
      const weekArray = (projects || []).filter(e => e.week === date);

      const dateActive = {
        x: 'Week ' + date,
        y: weekArray[0] ? weekArray[0].active : 0,
      };

      const dateScheduled = {
        x: 'Week ' + date,
        y: weekArray[0] ? weekArray[0].scheduled : 0,
      };

      const dateEnded = {
        x: 'Week ' + date,
        y: weekArray[0] ? weekArray[0].ended : 0,
      };

      const dateArchived = {
        x: 'Week ' + date,
        y: weekArray[0] ? weekArray[0].archived : 0,
      };

      active.push(dateActive);
      scheduled.push(dateScheduled);
      ended.push(dateEnded);
      archived.push(dateArchived);

      return active;
    });

    const monthProjects = [
      {
        id: statuses.translate('active', i18n),
        color: 'hsl(12, 74%, 59%)',
        data: active,
      },
      {
        id: statuses.translate('scheduled', i18n),
        color: 'hsl(200, 70%, 70%)',
        data: scheduled,
      },
      {
        id: statuses.translate('ended', i18n),
        color: 'hsl(203, 54%, 46%)',
        data: ended,
      },
      {
        id: statuses.translate('archived', i18n),
        color: 'hsl(200, 7%, 67%)',
        data: archived,
      },
    ];

    this.setState({
      data: monthProjects,
    });
  }

  displayYearView() {
    const { dashboardStore, i18n } = this.props;
    const { projects } = this.widgetData;
    const active = [];
    const scheduled = [];
    const ended = [];
    const archived = [];

    const time = new Date();
    const year = time.getFullYear();
    const dates = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

    dates.map(date => {
      const monthArray = (projects || [])
        .filter(e => e.month === date)
        .filter(e => e.year === year);

      const dateActive = {
        x: date + '/' + year,
        y: monthArray[0] ? monthArray[0].active : 0,
      };

      const dateScheduled = {
        x: date + '/' + year,
        y: monthArray[0] ? monthArray[0].scheduled : 0,
      };

      const dateEnded = {
        x: date + '/' + year,
        y: monthArray[0] ? monthArray[0].ended : 0,
      };

      const dateArchived = {
        x: date + '/' + year,
        y: monthArray[0] ? monthArray[0].archived : 0,
      };

      active.push(dateActive);
      scheduled.push(dateScheduled);
      ended.push(dateEnded);
      archived.push(dateArchived);

      return active;
    });

    const yearProjects = [
      {
        id: statuses.translate('active', i18n),
        color: 'hsl(12, 74%, 59%)',
        data: active,
      },
      {
        id: statuses.translate('scheduled', i18n),
        color: 'hsl(200, 70%, 70%)',
        data: scheduled,
      },
      {
        id: statuses.translate('ended', i18n),
        color: 'hsl(203, 54%, 46%)',
        data: ended,
      },
      {
        id: statuses.translate('archived', i18n),
        color: 'hsl(200, 7%, 67%)',
        data: archived,
      },
    ];

    this.setState({
      data: yearProjects,
    });
  }

  render() {
    const {
      dashboardStore,
      userStore: { isReadOnly },
      t,
      i18n,
      size,
      widgetItem,
      isFullScreen,
    } = this.props;
    const { projects } = this.widgetData;
    const active = projects ? projects[0].active : 0;
    const scheduled = projects ? projects[0].scheduled : 0;
    const period = 'Year';
    const ended = projects ? projects[0].ended : 0;
    const archived = projects ? projects[0].archived : 0;
    const link = dashboardStore.loadedTradingAccount
      ? '/projects/account/' + dashboardStore.loadedTradingAccount
      : '/projects';

    return (
      <Card className={styles[size]}>
        <CardHeader
          title={t('general:project_plural')}
          action={<WidgetActions widgetItem={widgetItem} size={size} isFullScreen={isFullScreen} />}
          className={styles['card-header']}
          classes={{ action: styles.cardHeaderAction }}
        />
        {(!isIE || size !== 'thumbnail') && (
          <React.Fragment>
            <Divider />
            <CardContent className={styles['card-content']}>
              {size !== 'thumbnail' && (
                <ChartLegend
                  position="center"
                  keys={[
                    {
                      label: t('general:dashboards.active_projects'),
                      color: 'hsl(12, 74%, 59%)',
                    },
                    {
                      label: t('general:projects.scheduled'),
                      color: 'hsl(200, 70%, 70%)',
                    },
                    {
                      label: t('general:dashboards.ended_projects'),
                      color: 'hsl(203, 54%, 46%)',
                    },
                    {
                      label: t('general:dashboards.archived_projects'),
                      color: 'hsl(200, 7%, 67%)',
                    },
                  ]}
                />
              )}
              <div className={styles['line-chart-container']}>
                {this.shouldLoadData && dashboardStore.loadingProjects ? (
                  <Loading />
                ) : (
                  <React.Fragment>
                    {size !== 'canvas-50' && (
                      <ChartKeys
                        widgetSize={size}
                        showPlaceholders={!this.shouldLoadData}
                        keys={[
                          {
                            to: `${link}/status/Active`,
                            label: statuses.translate('active', i18n),
                            color: 'hsl(12, 74%, 59%)',
                            total: active,
                          },
                          {
                            to: `${link}/status/Scheduled/${period}`,
                            label: statuses.translate('scheduled', i18n),
                            color: 'hsl(200, 70%, 70%)',
                            total: scheduled,
                          },
                          {
                            to: `${link}/status/Ended`,
                            label: statuses.translate('ended', i18n),
                            color: 'hsl(203, 54%, 46%)',
                            total: ended,
                          },
                          {
                            to: `${link}/status/Archived`,
                            label: statuses.translate('archived', i18n),
                            color: 'hsl(200, 7%, 67%)',
                            total: archived,
                          },
                        ]}
                      />
                    )}
                    <div className={styles['line-chart']}>
                      <NivoLineChart
                        data={this.state.data}
                        widgetSize={size}
                        axisLeft={{
                          renderTick: tick => {
                            return (
                              <g key={tick.key} transform={`translate(${tick.x},${tick.y})`}>
                                <text
                                  textAnchor="middle"
                                  alignmentBaseline="central"
                                  transform="translate(-12, 0)"
                                >
                                  {Number.isInteger(tick.key) ? tick.key : ''}
                                </text>
                              </g>
                            );
                          },
                        }}
                      />
                    </div>
                  </React.Fragment>
                )}
              </div>
              {size === 'thumbnail' ? null : (
                <div>
                  <ul className={styles['line-chart-filters']}>
                    <li
                      onClick={(e: any) => this.changeData('Week')}
                      className={this.state.status === 'Week' ? styles['filter-active'] : null}
                    >
                      {t('general:labels.week')}
                    </li>
                    <li
                      onClick={(e: any) => this.changeData('Month')}
                      className={this.state.status === 'Month' ? styles['filter-active'] : null}
                    >
                      {t('general:labels.month')}
                    </li>
                    <li
                      onClick={(e: any) => this.changeData('Year')}
                      className={this.state.status === 'Year' ? styles['filter-active'] : null}
                    >
                      {t('general:labels.year')}
                    </li>
                  </ul>
                </div>
              )}
            </CardContent>
          </React.Fragment>
        )}
        {size !== 'thumbnail' && (
          <div className={styles['button-container']}>
            <RequireForAccess permissions={createNewProject}>
              <Link to={isReadOnly ? '#' : '/projects/create'}>
                <Button color="primary" variant="contained" disabled={isReadOnly}>
                  {t('general:actions.add_project_plural')}
                </Button>
              </Link>
            </RequireForAccess>
          </div>
        )}
      </Card>
    );
  }
}
